import React, { useState, useEffect } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { AlertTriangle, TrendingUp, Shield, Globe, Info, ChevronDown, ChevronUp } from 'lucide-react';

const RiskRadarChart = () => {
  const [radarData, setRadarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMineral, setSelectedMineral] = useState('Lithium');
  const [selectedYear, setSelectedYear] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [availableMinerals, setAvailableMinerals] = useState([]);
  const [availableYears, setAvailableYears] = useState(['2024']);
  const [overlayMode, setOverlayMode] = useState(false);

  useEffect(() => {
    const fetchRadarData = async () => {
      // Only fetch if year is selected
      if (!selectedYear) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      try {
        if (overlayMode) {
          // Fetch all minerals for the selected year
          const allMineralData = [];
          for (const mineral of availableMinerals) {
            try {
              const response = await fetch(`/api/risk-radar?mineral=${mineral}&year=${selectedYear}`);
              if (response.ok) {
                const data = await response.json();
                allMineralData.push(data);
              }
            } catch (err) {
              console.warn(`Failed to fetch data for ${mineral}:`, err);
            }
          }
          
          if (allMineralData.length > 0) {
            setRadarData(allMineralData);
          } else {
            setError(`No risk radar data found for any minerals in ${selectedYear}`);
            setRadarData(null);
          }
        } else {
          // Fetch single mineral data
          if (!selectedMineral) {
            setLoading(false);
            return;
          }
          
          const response = await fetch(`/api/risk-radar?mineral=${selectedMineral}&year=${selectedYear}`);
          if (response.ok) {
            const data = await response.json();
            setRadarData([data]);
          } else if (response.status === 404) {
            setError(`No risk radar data found for ${selectedMineral} in ${selectedYear}`);
            setRadarData(null);
          } else {
            setError('Failed to fetch radar data');
            setRadarData(null);
          }
        }
      } catch (err) {
        setError(err.message);
        setRadarData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRadarData();
  }, [selectedMineral, selectedYear, overlayMode, availableMinerals]);

  useEffect(() => {
    const fetchAvailableOptions = async () => {
      try {
        const response = await fetch('/api/risk-radar/options');
        if (response.ok) {
          const data = await response.json();
          setAvailableMinerals(data.minerals || []);
          setAvailableYears(data.years || []);
          
          // Set default selections if available
          if (data.minerals && data.minerals.length > 0 && !selectedMineral) {
            setSelectedMineral(data.minerals[0]);
          }
          if (data.years && data.years.length > 0 && !selectedYear) {
            setSelectedYear(data.years[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch available options:', err);
      }
    };

    fetchAvailableOptions();
  }, []);

  const getRiskColor = (overallScore) => {
    if (overallScore >= 4.1) return "#b91c1c"; // Red - Critical
    if (overallScore >= 3.1) return "#d97706"; // Amber - Act
    if (overallScore >= 2.1) return "#ca8a04"; // Yellow - Watch
    return "#15803d"; // Green - Monitor
  };

  // Define specific colors for each mineral (only used in overlay mode)
  const getMineralColor = (mineralName) => {
    const mineralColors = {
      'Nickel': '#1d3d63',
      'Graphite': '#00847E', 
      'Lithium': '#883039',
      'Rare Earth': '#B16214',
      'Cobalt': '#286BBB'
    };
    return mineralColors[mineralName] || '#6b7280'; // Default gray if mineral not found
  };

  const getRiskLevel = (overallScore) => {
    if (overallScore >= 4.1) return "Critical";
    if (overallScore >= 3.1) return "Act";
    if (overallScore >= 2.1) return "Watch";
    return "Monitor";
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel.toLowerCase()) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'act':
        return <TrendingUp className="h-4 w-4 text-orange-500" />;
      case 'watch':
        return <Shield className="h-4 w-4 text-yellow-500" />;
      case 'monitor':
        return <Globe className="h-4 w-4 text-green-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-400" />;
    }
  };

  const formatRadarData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    return data.map(mineralData => ({
      mineral: mineralData.mineral_name,
      overall: mineralData.overall_risk,
      data: [
        {
          axis: 'Supply',
          value: mineralData.supply_risk,
          fullMark: 5
        },
        {
          axis: 'Price',
          value: mineralData.price_risk,
          fullMark: 5
        },
        {
          axis: 'Political',
          value: mineralData.political_risk,
          fullMark: 5
        },
        {
          axis: 'Concentration',
          value: mineralData.concentration_risk,
          fullMark: 5
        }
      ]
    }));
  };

  const formatOverlayData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    
    // Create a data structure that Recharts can handle for overlay mode
    const axes = ['Supply', 'Price', 'Political', 'Concentration'];
    const overlayData = axes.map(axis => {
      const dataPoint = { axis };
      data.forEach(mineralData => {
        const value = mineralData[`${axis.toLowerCase()}_risk`];
        dataPoint[mineralData.mineral_name] = value;
      });
      return dataPoint;
    });
    
    return overlayData;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 shadow-lg transition-colors duration-200">
          <p className="text-gray-900 dark:text-white font-medium">{`${label} Risk`}</p>
          <p className="text-blue-600 dark:text-blue-400">
            {`Score: ${payload[0].value.toFixed(1)}/5`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">
            {!selectedMineral || !selectedYear ? 'Loading options...' : 'Loading Risk Radar...'}
          </span>
        </div>
      </div>
    );
  }

  if (!selectedYear) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Info className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Select Year</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please select a year to view the risk radar chart.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!overlayMode && !selectedMineral) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Info className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Select Mineral</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Please select a mineral to view the risk radar chart, or enable overlay mode to view all minerals.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Format chart data early to avoid reference errors
  const chartData = formatRadarData(radarData);
  const overlayData = formatOverlayData(radarData);
  const riskColor = chartData.length > 0 ? getRiskColor(chartData[0].overall) : '#15803d';
  const riskLevel = chartData.length > 0 ? getRiskLevel(chartData[0].overall) : 'Monitor';

  if (error || !radarData || chartData.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Risk Data Available</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error || (overlayMode 
                ? `No risk radar data found for any minerals in ${selectedYear}` 
                : `No risk radar data found for ${selectedMineral} in ${selectedYear}`
              )}
            </p>
            <p className="text-gray-600 dark:text-gray-500 text-sm mb-4">
              {overlayMode 
                ? "Please select a different year or populate the database with risk data for multiple minerals."
                : "Please select a different mineral or year, or populate the database with risk data."
              }
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {overlayMode 
            ? `Multi-Dimensional Risk Assessment — All Minerals (${selectedYear})` 
            : `Multi-Dimensional Risk Assessment — ${selectedMineral} (${selectedYear})`
          }
        </h2>
        <p>Supply • Price • Political • Concentration — 1–5 scale (higher = higher risk)</p>
        <p className="text-gray-600 dark:text-gray-400 text-xm mt-2">
          {overlayMode 
            ? "Shows the overall risk level of multiple critical minerals for the selected year. Each mineral is shown in a different color based on its overall risk level. Use the controls below to change the year or disable overlay mode to view individual minerals."
            : "Shows the overall risk level of a critical mineral based on supply availability, price volatility, political factors, and market concentration. Higher scores indicate higher risk and greater potential exposure."
          }
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <label className="text-gray-700 dark:text-gray-300 text-sm font-medium">Year:</label>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
            className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <option value="">Select year...</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-gray-700 dark:text-gray-300 text-sm font-medium">Mineral:</label>
          <select
            value={selectedMineral}
            onChange={(e) => {
              // Only set mineral if a valid option is selected (not the placeholder)
              if (e.target.value && e.target.value !== '') {
                setSelectedMineral(e.target.value);
              }
            }}
            disabled={overlayMode}
            className={`bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 ${overlayMode ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <option value="" disabled>Select mineral...</option>
            {availableMinerals.map(mineral => (
              <option key={mineral} value={mineral}>{mineral}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">
            Overlay Mode:
          </span>
          <button
            type="button"
            onClick={() => {
              setOverlayMode(!overlayMode);
              if (!overlayMode) {
                // Clear mineral selection when overlay is enabled
                setSelectedMineral('');
              } else {
                // When disabling overlay mode, select first available mineral if none selected
                if (!selectedMineral && availableMinerals.length > 0) {
                  setSelectedMineral(availableMinerals[0]);
                }
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              overlayMode ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                overlayMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className="text-gray-600 dark:text-gray-400 text-sm">
            {overlayMode ? 'All minerals' : 'Single mineral'}
          </span>
        </div>

        {/* Mineral Legend - positioned above chart when overlay mode is active */}
        {overlayMode && chartData.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-gray-700 dark:text-gray-300 text-sm font-medium">Minerals:</span>
            {chartData.map((mineralData, index) => {
              const mineralColor = getMineralColor(mineralData.mineral);
              return (
                <div key={`legend-${mineralData.mineral}-${index}`} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded border-2 shadow-sm"
                    style={{ 
                      backgroundColor: mineralColor,
                      borderColor: mineralColor,
                      boxShadow: `0 0 4px ${mineralColor}40`
                    }}
                  />
                  <span className="text-gray-600 dark:text-gray-400 text-sm">
                    {mineralData.mineral} ({mineralData.overall.toFixed(2)})
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Radar Chart */}
      <div className="mb-6">
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart 
              data={overlayMode ? overlayData : (chartData.length > 0 ? chartData[0].data : [])} 
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            >
              <PolarGrid stroke="#374151" />
              <PolarAngleAxis 
                dataKey="axis" 
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                className="text-gray-400"
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 5]} 
                tick={{ fill: '#6B7280', fontSize: 10 }}
                tickCount={6}
              />
              {overlayMode ? (
                // Overlay mode: show all minerals with specific mineral colors
                chartData.map((mineralData, index) => {
                  const mineralColor = getMineralColor(mineralData.mineral);
                  return (
                    <Radar
                      key={`${mineralData.mineral}-${index}`}
                      name={`${mineralData.mineral} (${mineralData.overall.toFixed(2)})`}
                      dataKey={mineralData.mineral}
                      stroke={mineralColor}
                      fill={mineralColor}
                      fillOpacity={0.15}
                      strokeWidth={2}
                      dot={{ fill: mineralColor, strokeWidth: 2, r: 4 }}
                    />
                  );
                })
              ) : (
                // Single mineral mode
                chartData.length > 0 ? (
                  <Radar
                    key="single-mineral"
                    name="Risk Score"
                    dataKey="value"
                    stroke={riskColor}
                    fill={riskColor}
                    fillOpacity={0.2}
                    strokeWidth={2}
                    dot={{ fill: riskColor, strokeWidth: 2, r: 4 }}
                  />
                ) : null
              )}
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      
      {/* Risk Level Legend - only show in single mineral mode */}
      {!overlayMode && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Risk Level Legend:</h4>
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-red-600 shadow-sm shadow-red-600/20"></div>
              <span className="text-gray-600 dark:text-gray-400">Critical (4.1-5.0)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-orange-500 shadow-sm shadow-orange-500/20"></div>
              <span className="text-gray-600 dark:text-gray-400">Act (3.1-4.0)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-yellow-500 shadow-sm shadow-yellow-500/20"></div>
              <span className="text-gray-600 dark:text-gray-400">Watch (2.1-3.0)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border-2 border-green-600 shadow-sm shadow-green-600/20"></div>
              <span className="text-gray-600 dark:text-gray-400">Monitor (1.0-2.0)</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Data Sources */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Data Sources</h4>
            <div className="space-y-3 text-xs text-gray-600 dark:text-gray-400">
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">Supply Risk Score - is based on Overall Import Dependency per Mineral and Top supplier market share %</div>
              </div>
              
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">Price Risk Score - is based on Monthly prices for last 12 months</div>
           
              </div>
              
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">Political Risk Score - is based on Top 3 supplier countries and their market shares (%) and Partner Risk scores</div>
                
              </div>
              
              <div>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-1">Concentration Risk Score - is based on Market share (%) of each supplier country</div>
                
              </div>
            </div>
          </div>
          
          {/* Risk Level Legend */}
          {/* <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Risk Level Classification</h4>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border-2 border-red-600 shadow-sm shadow-red-600/20"></div>
                  <span>Critical (4.1-5.0)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border-2 border-orange-500 shadow-sm shadow-orange-500/20"></div>
                  <span>Act (3.1-4.0)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border-2 border-yellow-500 shadow-sm shadow-yellow-500/20"></div>
                  <span>Watch (2.1-3.0)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded border-2 border-green-600 shadow-sm shadow-green-600/20"></div>
                  <span>Monitor (1.0-2.0)</span>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default RiskRadarChart;
